




import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchCategories, deleteCategory, swapCategoryOrder } from "../redux/actions/categoryActions";
import { MdDelete, MdEdit, MdArrowUpward, MdArrowDownward } from "react-icons/md";
import toast from "react-hot-toast";
import AddCategory from "../components/Category/AddCategory";
import EditCategory from "../components/Category/EditCategory";
import ConfirmDeleteModal from "../components/shared/ConfirmDeleteModalForothers.jsx";

const Categories = () => {
  const dispatch = useDispatch();
  const { categories, loading, error, status } = useSelector(
    (state) => state.categories
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const itemsPerPage = 10;

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchCategories());
    }
  }, [dispatch, status]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = categories.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(categories.length / itemsPerPage);

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const openEditModal = (category) => {
    setCategoryToEdit(category);
    setIsEditModalOpen(true);
  };

  const openDeleteConfirmModal = (category) => {
    setCategoryToDelete(category);
    setIsDeleteConfirmOpen(true);
  };

  const handleDelete = () => {
    dispatch(deleteCategory(categoryToDelete._id))
      .then(() => {
        toast.success("Category deleted successfully!");
        setIsDeleteConfirmOpen(false);
      })
      .catch((error) => {
        toast.error("Error deleting category.");
        setIsDeleteConfirmOpen(false);
      });
  };

  const handleSwap = (index, direction) => {
    const globalIndex = indexOfFirstItem + index;
    const targetIndex = direction === "up" ? globalIndex - 1 : globalIndex + 1;
    if (targetIndex < 0 || targetIndex >= categories.length) return;
    dispatch(swapCategoryOrder(categories[globalIndex]._id, categories[targetIndex]._id))
      .catch(() => toast.error("Failed to reorder categories."));
  };

  return (
    <div className="w-full h-auto bg-gray-100 p-4">
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
  <h1 className="text-2xl font-bold text-gray-900 mb-4 sm:mb-0">Category Management</h1>
  <button
    onClick={toggleModal}
    className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition duration-200"
  >
    Add Category
  </button>
</div>


      {isModalOpen && <AddCategory onCloseModal={toggleModal} />}
      {isEditModalOpen && (
        <EditCategory
          category={categoryToEdit}
          onCloseModal={() => setIsEditModalOpen(false)}
        />
      )}

      {isDeleteConfirmOpen && (
        <ConfirmDeleteModal
          isOpen={isDeleteConfirmOpen}
          onClose={() => setIsDeleteConfirmOpen(false)}
          onConfirm={handleDelete}
          message={`Are you sure you want to delete the category "${categoryToDelete?.name}"?`}
        />
      )}

      {loading && (
        <div className="flex justify-center items-center h-64">
          <p className="text-gray-600">Loading categories...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4">
          <p>Error loading categories: {error}</p>
        </div>
      )}

      {categories && categories.length > 0 ? (
        <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
          <table className="min-w-full">
            <thead className="bg-black text-white">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentItems.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50 transition duration-300">
                  <td className="px-6 py-4 text-sm text-gray-900">{item.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{item.description}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {new Date(item.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 flex space-x-4">
                    <button
                      onClick={() => handleSwap(index, "up")}
                      disabled={indexOfFirstItem + index === 0}
                      className="text-blue-500 hover:text-blue-700 disabled:opacity-30"
                      title="Move Up"
                    >
                      <MdArrowUpward className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleSwap(index, "down")}
                      disabled={indexOfFirstItem + index === categories.length - 1}
                      className="text-blue-500 hover:text-blue-700 disabled:opacity-30"
                      title="Move Down"
                    >
                      <MdArrowDownward className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => openDeleteConfirmModal(item)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <MdDelete className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => openEditModal(item)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <MdEdit className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex justify-between items-center p-4 border-t border-gray-200">
            <button
              onClick={handlePrevious}
              disabled={currentPage === 1}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition duration-300 disabled:opacity-50"
            >
              Previous
            </button>
            <p className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </p>
            <button
              onClick={handleNext}
              disabled={currentPage === totalPages}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition duration-300 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white p-6 rounded-lg shadow-lg text-center">
          <p className="text-gray-600">No categories available</p>
        </div>
      )}
    </div>
  );
};

export default Categories;